import React, { useEffect, useState } from 'react';
import {
  useNavigate, useParams, useSearchParams,
} from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import Page from 'modules/app/components/Page';
import PostView from 'components/shared/PostView';
import { DateInput } from 'components/shared/Input';
import CategoriesFilter from 'components/shared/CategoriesFilter';
import Skeleton from 'components/shared/Skeleton';
import PageContainer from 'components/shared/PageContainer';
import clsx from 'clsx';
import NoContent from 'components/shared/NoContent/NoContent';
import useGetPlaylistIdByName from 'sdk/hooks/post/useGetPlaylistIdByName';
import styles from './Playlist.module.scss';
import usePlaylistPosts from '../../../../sdk/hooks/feed/usePlaylistPosts';

function PlaylistSkeleton({ title }: { title: string }) {
  return (
    <PageContainer className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.h}>
          <div className={styles.title}>
            <IconArrowRight />
            {title ? (
              <span>
                {title}
              </span>
            ) : (
              <Skeleton style={{ width: '300px', display: 'inline-block' }} />
            )}
          </div>
        </div>
      </div>
      <div className={styles.playlistPosts}>
        {Array.from(Array(12).keys()).map((item: number) => (
          <div className={styles.playlistItem} key={item}>
            <Skeleton className={styles.playlistItemSkeleton} />
          </div>
        ))}
      </div>
    </PageContainer>
  )
}

function Playlist() {
  const { name } = useParams()
  const { data: id } = useGetPlaylistIdByName(name)

  const [searchParams] = useSearchParams()
  const asset = searchParams.get('asset')

  const navigate = useNavigate()

  const isDaily = id === '5'
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const { ref, inView } = useInView()

  const {
    loaded, posts, feedData, globalFeedData, playlistData, day, setDay,
    // @ts-ignore
  } = usePlaylistPosts(id, asset, activeCategory, isDaily, 12, activeCategory ? `cat-${activeCategory}` : 'global');

  // eslint-disable-next-line no-nested-ternary
  const feedIdUrl = playlistData.data?.name === 'New' ? 'global' : (id && id !== 'undefined') ? `playlist-${playlistData?.data?.id}` : `asset-${asset}`

  useEffect(() => {
    if (inView && (feedData.hasNextPage || globalFeedData.hasNextPage)) {
      feedData.fetchNextPage()
      globalFeedData.fetchNextPage()
    }
  }, [inView, feedData.hasNextPage, globalFeedData.hasNextPage])

  if (!loaded || !playlistData.isFetched) {
    return (<PlaylistSkeleton title={playlistData?.data?.name || ''} />)
  }

  return (
    <PageContainer className={styles.container}>
      {playlistData?.data?.bg_image ? (
        <div
          className={styles.background}
          style={{ backgroundImage: `url(${playlistData?.data?.bg_image})` }}
        />
      ) : null}
      <div className={styles.headerRow}>
        <div className={styles.h} onClick={() => navigate(-1)}>
          {playlistData?.data?.bg_image ? (
            <div className={styles.title}>
              <IconArrowRight />
            </div>
          ) : (
            <h1 className={styles.title}>
              <IconArrowRight />
              {loaded ? (playlistData?.data?.name || 'Trending') : null}
            </h1>
          )}
        </div>
        {!playlistData?.data?.bg_image ? (
          <CategoriesFilter
            value={activeCategory}
            onChange={setActiveCategory}
            playlistId={playlistData?.data?.id}
          />
        ) : null}
        {isDaily ? (
          <DateInput
            className={styles.dayInput}
            value={day}
            onChange={(value: Date | null) => {
              setDay(value)
            }}
            dateFormat="MMM, dd"
          />
        ) : null}
      </div>
      {playlistData?.data?.bg_image ? (
        <h1 className={styles.titleBig}>
          {playlistData?.data?.name || 'Trending'}
        </h1>
      ) : null}
      <div className={clsx(
        styles.playlistPosts,
        (feedData.isLoading || globalFeedData.isLoading) && styles.playlistPosts_loading,
      )}
      >
        {posts.map((post: any) => (
          <div className={styles.playlistItem} key={post?.id}>
            <PostView
              feedId={feedIdUrl}
              feedName="global_flat"
              data={post}
              isDaily={isDaily}
            />
          </div>
        ))}
      </div>
      <div ref={ref} />
      {!posts?.length && (
        <div className={(feedData.isLoading || globalFeedData.isLoading) ? styles.playlistPosts_loading : ''}>
          <NoContent
            message="No data satisfying the filters"
          />
        </div>
      )}
    </PageContainer>
  )
}

function PlaylistPage() {
  return (
    <Page activeLink="/">
      <Playlist />
    </Page>
  )
}

export default PlaylistPage
